import React from "react";
import { TaskPriority as TaskPriorityEnum } from "@common/types/task";
import type { TooltipTriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { Picker } from "@design-system/Picker/Picker.lib";
import { TaskPriority } from "./TaskPriority";
import { TASK_PRIORITY_CONFIG } from "./taskPriority.constant";

interface TaskPrioritySelectProps {
    value: TaskPriorityEnum | null | undefined;
    onValueChange: (priority: TaskPriorityEnum | null) => void;
    placeholder?: string;
    /** A Picker trigger part (e.g. `Picker.CellContent`) replacing the default button. */
    trigger?: React.ReactNode;
    buttonProps?: TooltipTriggerButtonProps;
}

export const NO_PRIORITY_LABEL = "No priority";

export const ORDERED_TASK_PRIORITIES: readonly TaskPriorityEnum[] = [
    TaskPriorityEnum.URGENT,
    TaskPriorityEnum.HIGH,
    TaskPriorityEnum.MEDIUM,
    TaskPriorityEnum.LOW,
];

export const TaskPrioritySelect: React.FC<TaskPrioritySelectProps> = ({
    value,
    onValueChange,
    placeholder = "Set priority",
    trigger,
    buttonProps,
}) => {
    const selectedPriority = value ?? null;
    const {
        tooltip: triggerTooltip,
        variant: triggerVariant = "primary",
        ...triggerButtonRest
    } = buttonProps ?? {};

    return (
        <Picker.Select
            value={selectedPriority}
            onValueChange={(nextPriority) => onValueChange(nextPriority)}
        >
            {trigger ?? (
                <Picker.ButtonTrigger
                    render={
                        <Picker.Button
                            aria-label={
                                selectedPriority !== null
                                    ? TASK_PRIORITY_CONFIG[selectedPriority]
                                          .label
                                    : undefined
                            }
                            {...triggerButtonRest}
                            variant={triggerVariant}
                            size={triggerButtonRest.size ?? "md"}
                        />
                    }
                    value={
                        selectedPriority !== null ? (
                            <TaskPriority
                                priority={selectedPriority}
                                variant="textOnly"
                            />
                        ) : (
                            NO_PRIORITY_LABEL
                        )
                    }
                    placeholder={placeholder}
                    tooltip={triggerTooltip}
                />
            )}
            <Picker.Popup sideOffset={4}>
                <Picker.List>
                    <Picker.Item
                        leading={
                            <TaskPriority priority={null} variant="iconOnly" />
                        }
                        onSelect={() => onValueChange(null)}
                    >
                        {NO_PRIORITY_LABEL}
                    </Picker.Item>
                    {ORDERED_TASK_PRIORITIES.map((priority) => (
                        <Picker.Item
                            key={priority}
                            value={priority}
                            leading={
                                <TaskPriority
                                    priority={priority}
                                    variant="iconOnly"
                                />
                            }
                        >
                            {TASK_PRIORITY_CONFIG[priority].label}
                        </Picker.Item>
                    ))}
                </Picker.List>
            </Picker.Popup>
        </Picker.Select>
    );
};
